// src/pages/SelectPlan.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Crown, Check, Sparkles, Users, User, ArrowRight, Shield, HeartHandshake } from 'lucide-react';
import Footer from '../components/Footer';
import { COURSE_PACKAGES, getSortedPackages } from '../config/coursePackages';
import { getChildren } from '../utils/childrenHelper';

function SelectPlan() {
  const { user, authLoading } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState(null);

  // Get packages from configuration
  const packages = COURSE_PACKAGES;
  const childPackages = getSortedPackages().filter(pkg => pkg.audience !== 'adult');
  const adultPackages = getSortedPackages().filter(pkg => pkg.audience === 'adult');

  // Redirect authenticated users appropriately
  useEffect(() => {
    const checkUserStatus = async () => {
      if (authLoading) {
        setIsChecking(true);
        return;
      }
      
      if (user) {
        try {
          setIsChecking(true);
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const children = getChildren(userData);
            
            // Check if user has any active courses
            const hasActiveCourse = children.some(child => 
              child.course?.status === 'active'
            );
            
            // Check if user has pending status
            const hasPendingCourse = children.some(child =>
              child.course?.status === 'pending'
            );
            
            if (hasActiveCourse) {
              // User has active course, redirect to home
              navigate('/', { replace: true });
              return;
            } else if (hasPendingCourse) {
              // User has pending course, redirect to complete subscription
              navigate('/complete-subscription', { replace: true });
              return;
            }
            // Otherwise, let them stay on this page to view plans
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
          setError('Failed to load user data');
        } finally {
          setIsChecking(false);
        }
      } else {
        // Not logged in, show page
        setIsChecking(false);
      }
    };

    checkUserStatus();
  }, [user, authLoading, navigate]);

  const handleGetStarted = () => {
    navigate('/create-account');
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  // Loading state
  if (authLoading || isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-purple-600 text-xl font-bold">Loading...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md">
          <div className="text-red-500 text-xl font-bold mb-4">Oops!</div>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const renderCard = (plan) => (
    <div
      key={plan.id}
      className="relative bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border-4 border-transparent"
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
            <Crown size={16} />
            Most Popular
          </div>
        </div>
      )}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          {plan.type === 'group' ? (
            <Users className="text-purple-600" size={48} />
          ) : (
            <User className="text-purple-600" size={48} />
          )}
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
        <p className="text-gray-600">{plan.description}</p>
        {plan.audience === 'adult' && (
          <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
            Adults 18+
          </span>
        )}
        <div className="mt-4">
          <span className="text-4xl font-black text-purple-600">${plan.price}</span>
          <span className="text-gray-500 ml-2">CAD {plan.audience === 'adult' ? 'per learner' : 'per child'}</span>
        </div>
      </div>
      <div className="space-y-3 mb-6">
        {plan.features.map((feature, index) => (
          <div key={index} className="flex items-start gap-3">
            <Check className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
            <span className="text-gray-700 text-sm">{feature}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
            <Sparkles className="text-yellow-400" size={24} />
            <span className="text-white font-bold text-lg">Choose Your Learning Journey</span>
            <Sparkles className="text-yellow-400" size={24} />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 drop-shadow-2xl">
            Choose Your Arabic Adventure
          </h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">
            Start your Arabic learning journey — engaging courses for children and adults.
          </p>
        </div>

        {/* Plan Comparison Cards - Non-interactive Showcase */}
        <div className="mb-12">

          {/* Children's Section */}
          {childPackages.length > 0 && (
            <div className="mb-8">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white">For Young Learners</h2>
                <p className="text-purple-100 mt-2">Courses designed for children ages 5–15</p>
              </div>
              <div className="max-w-4xl mx-auto grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {childPackages.map(plan => renderCard(plan))}
              </div>
            </div>
          )}

          {/* Adult Section */}
          {adultPackages.length > 0 && (
            <div className="mt-16">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white">For Adult Learners</h2>
                <p className="text-purple-100 mt-2">Learning Arabic as an adult? This one's for you.</p>
              </div>
              <div className={
                adultPackages.length === 1
                  ? 'max-w-sm mx-auto'
                  : 'max-w-3xl mx-auto grid md:grid-cols-2 gap-8'
              }>
                {adultPackages.map(plan => renderCard(plan))}
              </div>
            </div>
          )}

        </div>

        {/* Action Buttons */}
        <div className="max-w-md mx-auto space-y-4">
          {/* Get Started Button */}
          <button 
            onClick={handleGetStarted}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            <div className="flex items-center justify-center gap-2">
              <ArrowRight size={20} />
              Get Started
            </div>
          </button>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-purple-100 text-sm mb-3">
              Already have an account?{' '}
              <button 
                onClick={handleBackToLogin}
                className="text-yellow-300 hover:text-yellow-100 font-semibold hover:underline"
              >
                Sign In
              </button>
            </p>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="max-w-4xl mx-auto mt-12 grid md:grid-cols-3 gap-6 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
            <Shield className="text-green-400 mx-auto mb-3" size={32} />
            <h4 className="text-white font-bold mb-2">Secure Payment</h4>
            <p className="text-purple-100 text-sm">Protected by Stripe encryption</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
            <Crown className="text-yellow-400 mx-auto mb-3" size={32} />
            <h4 className="text-white font-bold mb-2">Professional Teachers</h4>
            <p className="text-purple-100 text-sm">Certified Arabic instructors</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
            <HeartHandshake className="text-purple-300 mx-auto mb-3" size={32} />
            <h4 className="text-white font-bold mb-2">Join our Community</h4>
            <p className="text-purple-100 text-sm">Trusted by 630+ parents</p>
          </div>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}

export default SelectPlan;